import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { isTokenExpired, RefreshTokenResult } from "@kinde-oss/kinde-auth-react/utils";
import { config } from "@noted/configuration";
import { captureException } from "@sentry/react";
import { ComponentProps, useCallback, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { restPoster } from "~/api/fetchers";
import AuthProvider, { UseAuthType } from "~/components/auth-provider/index";
import {
  clearContextSession,
  getContextSession,
  getLoginState,
} from "~/components/auth-provider/shared";
import { useLogoutTracker } from "~/components/auth-provider/use-logout-tracker";
import { isDevelopment } from "~/utilities/is-development";

export function KindeAuthProvider({ children }: ComponentProps<typeof AuthProvider>) {
  const navigate = useNavigate();
  return (
    <KindeProvider
      audience={config.auth.audience}
      clientId={config.auth.clientId}
      domain={config.auth.domain}
      redirectUri={config.appUrl}
      logoutUri={config.landingUrl}
      useInsecureForRefreshToken={isDevelopment()}
      callbacks={{
        onSuccess: (_user, state) => {
          if (state?.redirectTo && typeof state.redirectTo === "string") {
            navigate(decodeURIComponent(state.redirectTo));
          }
        },
        onError: (error, _state, context) => {
          captureException(error, { tags: { context: "kinde_auth_error" }, extra: { context } });
        },
      }}
    >
      {children}
    </KindeProvider>
  );
}

// Refresh slightly before expiry so the token is still valid when the request reaches the API
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 30;
// Gives a just-woken device time to reconnect before a failed refresh is treated as final
const REFRESH_RETRY_DELAY_MS = 2000;
// Long enough to sign in through Kinde's popup; a redirect unloads the page well before this
const LOGIN_GUARD_MS = 5 * 60 * 1000;

let refreshInFlight: Promise<RefreshTokenResult> | null = null;
let loginStartedAt: number | null = null;

function isLoginInProgress() {
  return loginStartedAt !== null && Date.now() - loginStartedAt < LOGIN_GUARD_MS;
}

async function refreshWithRetry(refresh: () => Promise<RefreshTokenResult>) {
  const result = await refresh();
  if (result.success || !navigator.onLine) {
    return result;
  }
  await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
  return refresh();
}

/** Kinde does not dedupe refreshes; concurrent ones race on the rotated refresh token */
function refreshTokenOnce(refresh: () => Promise<RefreshTokenResult>) {
  refreshInFlight ??= refreshWithRetry(refresh).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Resets module state between tests */
export function resetKindeAuthState() {
  refreshInFlight = null;
  loginStartedAt = null;
}

export function useKindeAuthHook(): Omit<UseAuthType, "session"> {
  const {
    isAuthenticated,
    isLoading,
    getAccessToken,
    refreshToken,
    login: kindeLogin,
    logout: kindeLogout,
  } = useKindeAuth();

  // Kinde's login gets a new identity on every provider render, so call the latest one via a ref
  const kindeLoginRef = useRef(kindeLogin);
  useLayoutEffect(() => {
    kindeLoginRef.current = kindeLogin;
  }, [kindeLogin]);

  // Starts the login flow at most once. Each Kinde login call writes a new PKCE code verifier, so a
  // second call before the redirect makes the token exchange fail with invalid_grant
  const login = useCallback(async (options: Parameters<UseAuthType["login"]>[0]) => {
    if (isLoginInProgress() || useLogoutTracker.getState().logoutHasStarted) {
      return;
    }
    loginStartedAt = Date.now();
    try {
      await kindeLoginRef.current(options);
      // Inside an iframe Kinde signs in through a popup and the page stays loaded, so allow later
      // logins. A redirect is already unloading the page, so keep the guard for that case
      if (window.self !== window.top) {
        loginStartedAt = null;
      }
    } catch (err) {
      loginStartedAt = null;
      throw err;
    }
  }, []);

  const reauthenticate = useCallback(() => {
    void login({ state: getLoginState(window.location.pathname, window.location.search) });
  }, [login]);

  // Throws rather than returning an empty token, so requests are never sent unauthenticated
  const getToken = useCallback(async () => {
    if (isLoginInProgress()) {
      throw new Error("Re-authentication in progress");
    }

    try {
      // Kinde's getAccessToken returns the stored token without checking expiry, and its refresh
      // timer can fire late (e.g. after the device sleeps), so refresh on demand if needed
      if (await isTokenExpired({ threshold: TOKEN_EXPIRY_THRESHOLD_SECONDS })) {
        const result = await refreshTokenOnce(() =>
          refreshToken({ domain: config.auth.domain, clientId: config.auth.clientId })
        );
        if (!result.success) {
          throw new Error(`Kinde token refresh failed: ${result.error ?? "unknown error"}`);
        }
      }

      const token = await getAccessToken();
      if (!token) {
        throw new Error("Kinde getAccessToken returned null/undefined");
      }
      return token;
    } catch (err) {
      // Signing in again needs the network, so keep the user (and any unsaved work) on the page.
      // React Query refetches queries on reconnect, which refreshes the token
      if (!navigator.onLine) {
        throw err;
      }
      captureException(err, { tags: { context: "kinde_get_token" } });
      reauthenticate();
      throw err;
    }
  }, [getAccessToken, refreshToken, reauthenticate]);

  const logout = useCallback(async () => {
    try {
      if (getContextSession()) {
        await restPoster("/v1/auth/logout");
      }
    } finally {
      clearContextSession();
      await kindeLogout({ allSessions: true });
    }
  }, [restPoster, clearContextSession, kindeLogout, config.landingUrl]);

  return {
    isAuthenticated,
    isLoading,
    getToken,
    login,
    logout,
  };
}
